// One skinned mesh per character with its colours baked into vertex colours.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { M } from '../../lib/factory.js';
import { toonify } from '../../core/optimise.js';
import { H, clip, models } from './loader.js';
import { retarget, wq } from './retarget.js';
import { ARM_K, LEG_K, makeWalk } from './animation.js';

export let prepared;

export function prepareCharacters() {
  // One skinned mesh per character: material colours baked into vertex colours, so it costs one draw call.
  prepared = models.map((gltf) => {
    const root = gltf.scene;
    root.updateMatrixWorld(true);
    const parts = [],
      ranges = [];
    let skeleton = null,
      bindMatrix = null,
      at = 0;
    root.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      skeleton ??= o.skeleton;
      bindMatrix ??= o.bindMatrix;
      if (/^Backpack/.test(o.name)) return; // the adventurer's huge hiking pack: dropped
      const g = o.geometry.clone(),
        n = g.attributes.position.count,
        col = new Float32Array(n * 3),
        c = o.material.color || new THREE.Color(1, 1, 1);
      for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
      for (const k of Object.keys(g.attributes))
        if (!['position', 'normal', 'skinIndex', 'skinWeight'].includes(k)) g.deleteAttribute(k);
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      const ng = g.index ? g.toNonIndexed() : g;
      parts.push(ng);
      // vertex range of each material, so clones can be re-dressed
      const count = ng.attributes.position.count;
      ranges.push({ name: o.material.name, mesh: o.name, start: at, count });
      at += count;
    });
    const toRemove = [];
    root.traverse((o) => {
      if (o.isSkinnedMesh) toRemove.push(o);
    });
    const merged = new THREE.SkinnedMesh(
      mergeGeometries(parts),
      M(0xffffff, { vertexColors: true, flatShading: false, roughness: 0.7 }),
    );
    toRemove[0].parent.add(merged);
    toRemove.forEach((o) => o.parent.remove(o));
    merged.bind(skeleton, bindMatrix);
    toonify(merged.material);
    // bind-pose bounds in world space (SkinnedMesh.computeBoundingBox gives NaN for some of these models)
    root.updateMatrixWorld(true);
    const box = new THREE.Box3()
      .setFromBufferAttribute(merged.geometry.attributes.position)
      .applyMatrix4(merged.bindMatrix)
      .applyMatrix4(merged.parent.matrixWorld);
    // Stylised kit proportions (legs over half the height, long arms) read as spidery at street scale:
    // shorten the limbs on the skeleton (tracks are rotation-only, so the scale sticks) and let the feet back down.
    const bone = (n) => root.getObjectByName(n),
      legTop = bone('UpperLegL').getWorldPosition(new THREE.Vector3()).y,
      rise = (1 - LEG_K) * (legTop - box.min.y);
    for (const s of ['L', 'R']) {
      bone('UpperLeg' + s).scale.setScalar(LEG_K);
      bone('UpperArm' + s).scale.setScalar(ARM_K);
    }
    // relaxed hands: the bind pose has flat, spread fingers (T-pose, palms down), so curl them about the forward axis
    const RQ = wq(root);
    root.traverse((o) => {
      if (!o.isBone || !/^(Index|Middle|Ring|Pinky|Thumb)/.test(o.name)) return;
      const pq = wq(o.parent),
        ax = new THREE.Vector3(0, 0, 1).applyQuaternion(RQ),
        q = new THREE.Quaternion().setFromAxisAngle(ax, (o.name.endsWith('L') ? -1 : 1) * 0.5);
      // the curl axis in the parent's frame, kept so a grip can curl the fingers further later (clones copy userData)
      o.userData.curl = ax.clone().applyQuaternion(pq.clone().invert()).toArray();
      o.userData.sg = o.name.endsWith('L') ? -1 : 1;
      if (/^Thumb/.test(o.name)) {
        o.updateMatrix();
        o.matrixAutoUpdate = false;
        return;
      }
      o.quaternion.premultiply(pq.clone().invert().multiply(q).multiply(pq));
      o.updateMatrixWorld(true);
      o.updateMatrix();
      o.matrixAutoUpdate = false; // fingers never animate: keep their local matrix (clones copy it)
    });
    // bind-pose height of every vertex (before the limb scaling), used to recolour part of a garment
    const bm = merged.bindMatrix.clone().premultiply(merged.parent.matrixWorld),
      bp = merged.geometry.attributes.position,
      bindY = new Float32Array(bp.count),
      tv = new THREE.Vector3();
    for (let i = 0; i < bp.count; i++) bindY[i] = tv.fromBufferAttribute(bp, i).applyMatrix4(bm).y;
    const idle = retarget(clip('Idle_Neutral'), root);
    return {
      root,
      ranges,
      bindY,
      k: H / (box.max.y - box.min.y - rise),
      y0: -(box.min.y + rise),
      walk: makeWalk(root, idle),
      idle,
    };
  });
}
